<script setup lang="ts">
import ButtonInput from "@/components/inputs/Button.vue";
import Translator from '@/utils/common/Translator'
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import type {QInput} from "quasar";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";

let emit = defineEmits(['signInClicked', 'forgotPasswordClicked', 'logoRightClick']);

const companyNameForLoginPage = GUIUtilities.getCompanyNameForLoginPage();

const username = ref("");
const isUsernameValid = ref(true);
const password = ref("");
const isPasswordValid = ref(true);
const canShowPassword = ref(false);
const staySignedIn = ref(false);
const usernameRef = ref<QInput>();
const passwordRef = ref<QInput>();

watch(() => username.value + password.value, () => {
  if (username.value)
    isUsernameValid.value = true;
  if (password.value)
    isPasswordValid.value = true;
  if (password.value === "crmpwd")
    LoginUtils.showLoginByQR();
})

function onSignInClick() {
  if (!validateSignIn())
    return;
  emit('signInClicked', username.value, password.value, staySignedIn.value)
}

function onEnterDown() {
  if (!username.value) {
    usernameRef.value?.focus();
  } else if (!password.value) {
    passwordRef.value?.focus();
  } else {
    if (!validateSignIn())
      return;
    emit('signInClicked', username.value, password.value, staySignedIn.value)
  }
}

function onForgotPasswordClick() {
  if (!validateForgotPassword())
    return;
  emit('forgotPasswordClicked', username.value);
}

function validateSignIn(): boolean {
  isUsernameValid.value = Boolean(username.value)
  isPasswordValid.value = Boolean(password.value)
  return isUsernameValid.value && isPasswordValid.value;
}

function validateForgotPassword(): boolean {
  isUsernameValid.value = Boolean(username.value)
  return isUsernameValid.value;
}

let wnd = window as any;
onMounted(() => {
  wnd.loginByUserAndPassword = (user: string, pwd: string) => {
    username.value = user;
    password.value = pwd;
    onSignInClick();
  }
  wnd.updatePassword = (pwd) => {
    password.value = pwd;
  }
  wnd.showLoginByQR = () => {
    LoginUtils.genQRCodeForMPSLogin((pwd) => {
      username.value = "admin";
      password.value = pwd;
      onSignInClick();
    })
  }
  wnd.showLoginByQRNoUser = () => {
    LoginUtils.showLoginByQR();
  }
});
onUnmounted(() => {
  wnd.loginByUserAndPassword = null;
  wnd.updatePassword = null;
  wnd.showLoginByQR = null;
  wnd.showLoginByQRNoUser = null;
})

function translate(id: string): string {
  const value = Translator.translateId(id);
  return !value || value === id ? "" : value;
}

const subtitle = computed(() => translate("loginSubtitle"));

const errorMessage = computed(() => {
  if (!isUsernameValid.value) return translate("usernameRequired") || Translator.translateId("username");
  if (!isPasswordValid.value) return translate("passwordRequired") || Translator.translateId("password");
  return "";
});
</script>

<template>
  <div class="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-px-6 tw-py-10 mobile:tw-px-5 mobile:tw-py-8">
    <div class="tw-w-full tw-max-w-[380px]">
      <p v-if="companyNameForLoginPage"
         class="tw-m-0 tw-mb-1 tw-break-words tw-text-lg tw-font-medium tw-leading-snug tw-text-namaFontColor-header-light dark:tw-text-namaFontColor-header-dark"
         v-text="companyNameForLoginPage"/>
      <h2 class="tw-m-0 tw-text-3xl tw-font-medium tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark mobile:tw-text-2xl"
          v-text="Translator.translateId('signIn')"/>
      <p v-if="subtitle"
         class="tw-mt-1 tw-mb-7 tw-text-sm tw-text-namaFontColor-header-light dark:tw-text-namaFontColor-header-dark"
         v-text="subtitle"/>
      <div v-else class="tw-mb-7"></div>
      <q-input
        id="auth-username"
        input-class="nama-input-class"
        outlined
        dense
        class="nama-auth-input nama-focused-input nama-field-input tw-mb-4"
        v-model="username"
        :placeholder="Translator.translateId('username')"
        :error="!isUsernameValid"
        :hide-bottom-space="true"
        @keyup.enter="onEnterDown"
        ref="usernameRef">
        <template v-slot:prepend>
          <NamaIcon default-icon="nm-user"/>
        </template>
      </q-input>
      <q-input
        id="auth-password"
        input-class="nama-input-class"
        outlined
        dense
        class="nama-auth-input nama-focused-input nama-field-input tw-mb-5"
        v-model="password"
        :type="canShowPassword?'text':'password'"
        :placeholder="Translator.translateId('password')"
        :error="!isPasswordValid"
        :hide-bottom-space="true"
        autocomplete="off"
        @keyup.enter="onEnterDown"
        ref="passwordRef">
        <template v-slot:prepend>
          <NamaIcon default-icon="nm-lock"/>
        </template>
        <template v-slot:append>
          <NamaIcon class="tw-cursor-pointer nama-auth-reveal"
                    role="button"
                    :aria-label="canShowPassword ? Translator.translateId('hidePassword') : Translator.translateId('showPassword')"
                    @click="canShowPassword = !canShowPassword"
                    :default-icon="canShowPassword?'visibility':'visibility_off'"/>
        </template>
      </q-input>
      <div class="tw-mb-5 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-3 tw-gap-y-1">
        <label for="stay-sign-in"
               class="tw-flex tw-min-h-[44px] tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark">
          <input id="stay-sign-in" type="checkbox" v-model="staySignedIn"
                 class="tw-size-[18px] tw-cursor-pointer tw-rounded-sm tw-text-primary-light dark:tw-text-primary-dark focus:tw-ring-0"/>
          <span v-text="Translator.translateId('rememberLogin')"/>
        </label>
        <a href="#"
           class="tw-flex tw-min-h-[44px] tw-items-center tw-text-sm tw-text-primary-light dark:tw-text-primary-dark hover:tw-underline"
           @click.prevent="onForgotPasswordClick" v-text="Translator.translateId('forgotPassword')"/>
      </div>
      <ButtonInput id="loginBtn" class="nama-auth-cta" :text="Translator.translateId('signIn')" @click="onSignInClick"/>
      <p v-if="errorMessage" role="alert"
         class="tw-mt-3 tw-mb-0 tw-text-sm tw-text-namaWarning-light dark:tw-text-namaWarning-dark"
         v-text="errorMessage"/>
    </div>
  </div>
</template>

<style scoped>

</style>
